import './Icon.scss';

import { AndroidLogo } from './AndroidLogo';
import { AppleLogo } from './AppleLogo';
import { AppStoreIcon } from './AppStoreIcon';
import BurgerMenuExpandedIcon from './BurgerMenuExpandedIcon';
import BurgerMenuIcon from './BurgerMenuIcon';
import CardPayment from './CardPayment';
import CheckIcon from './CheckIcon';
import CompleteIcon from './CompleteIcon';
import DoubleDocumentIcon from './DoubleDocumentIcon';
import EmailIcon from './EmailIcon';
import EsimIcon from './EsimIcon';
import FiveStarsIcon from './FiveStarsIcon';
import { GiftCardBig } from './GiftCardBig';
import { GiftCardSmall } from './GiftCardSmall';
import GlobeSimpleIcon from './GlobeSimpleIcon';
import { GooglePlayIcon } from './GooglePlayIcon';
import GoogleRating from './GoogleRating';
import LineIcon from './LineIcon';
import MyAccountIcon from './MyAccountIcon';
import { ApplePay, GooglePay, PayPal, Stripe } from './Payments';
import ProcessingIcon from './ProcessingIcon';
import SimOutline from './SimOutline';
import {
  Facebook,
  FacebookColored,
  Instagram,
  InstagramColored,
  LinkedIn,
  LinkedinColored,
  ShareIconColored,
  TikTok,
  TikTokColored,
  X,
  XColored,
  Youtube,
  YoutubeColored,
} from './SNS';
import { StarIcon } from './StarIcon';
import StorefrontIcon from './StorefrontIcon';
import TextIcon from './TextIcon';
import TripadvisorRatingIcon from './TripadvisorRating';

export type IconName = keyof typeof nameToIconMap;

export type IconColor =
  | 'charcoal'
  | 'none'
  | 'color-neutral-7'
  | 'font-color-primary'
  | 'white'
  | 'color-quaternary';

export const COLOR_MAPPINGS: { [K in IconColor]: string } = {
  charcoal: '#333333',
  none: 'none',
  'color-neutral-7': 'var(--color-neutral-7)',
  'font-color-primary': 'var(--font-color-primary, #424242)',
  white: 'var(--white)',
  'color-quaternary': 'var(--color-quaternary)',
};

type Props = {
  name: IconName;
  color?: IconColor;
} & Record<string, any>;

export const nameToIconMap = {
  appStoreIcon: AppStoreIcon,
  applePay: ApplePay,
  burgerMenu: BurgerMenuIcon,
  burgerMenuExpanded: BurgerMenuExpandedIcon,
  cardPayment: CardPayment,
  check: CheckIcon,
  completeIcon: CompleteIcon,
  doubleDocumentIcon: DoubleDocumentIcon,
  emailIcon: EmailIcon,
  esimIcon: EsimIcon,
  facebook: Facebook,
  facebookColored: FacebookColored,
  fiveStarsIcon: FiveStarsIcon,
  giftCardBig: GiftCardBig,
  giftCardSmall: GiftCardSmall,
  globeSimpleIcon: GlobeSimpleIcon,
  googlePay: GooglePay,
  googlePlayIcon: GooglePlayIcon,
  googleRatingIcon: GoogleRating,
  instagram: Instagram,
  instagramColored: InstagramColored,
  lineIcon: LineIcon,
  linkedIn: LinkedIn,
  linkedinColored: LinkedinColored,
  myAccount: MyAccountIcon,
  payPal: PayPal,
  processingIcon: ProcessingIcon,
  shareIconColored: ShareIconColored,
  simOutline: SimOutline,
  starIcon: StarIcon,
  storeFront: StorefrontIcon,
  stripe: Stripe,
  textIcon: TextIcon,
  tikTok: TikTok,
  tikTokColored: TikTokColored,
  tripadvisorRatingIcon: TripadvisorRatingIcon,
  x: X,
  xColored: XColored,
  youtube: Youtube,
  youtubeColored: YoutubeColored,
  androidLogo: AndroidLogo,
  appleLogo: AppleLogo,
};

/**
 * Used for custom icons. Make sure an icon is not available to be imported
 * from @phosphor-icons/react package before adding an icon. A searchable list
 * of all the available icons can be found here: https://phosphoricons.com/
 */
const Icon = (props: Props) => {
  const { name, color, ...iconProps } = props;
  const IconComponent = nameToIconMap[name];
  const iconFill = color && COLOR_MAPPINGS[color];

  return <IconComponent {...(iconProps as any)} color={iconFill} />;
};

export default Icon;
